import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { heroes } from '@/mocks/heroes';

export default function HeroCarousel() {
  const [active, setActive] = useState(0);

  const goTo = useCallback((index: number) => {
    setActive((index + heroes.length) % heroes.length);
  }, []);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % heroes.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, []);

  return (
    <section className="relative h-[440px] w-full overflow-hidden bg-accent-950 md:h-[580px]">
      {heroes.map((hero, index) => {
        const comic = comics.find((item) => item.id === hero.comicId);
        if (!comic) return null;
        const isActive = index === active;
        return (
          <div
            key={hero.comicId}
            className={`absolute inset-0 transition-opacity duration-700 ${
              isActive ? 'opacity-100' : 'pointer-events-none opacity-0'
            }`}
          >
            <img
              src={hero.image}
              alt={`${comic.title}焦点推荐`}
              title={`${comic.title} 双女主韩漫免费阅读`}
              className="h-full w-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/20"></div>
            <div className="absolute inset-0 flex items-center">
              <div className="mx-auto w-full px-4 md:px-6">
                <div className="max-w-xl">
                  <span className="mb-4 inline-flex items-center gap-1 rounded-full bg-primary-500 px-3 py-1 text-xs font-semibold text-background-50">
                    <i className="ri-fire-line"></i>
                    编辑精选 · 双女主韩漫
                  </span>
                  <h2 className="text-3xl font-bold leading-tight text-background-50 md:text-5xl">
                    {comic.title}
                  </h2>
                  <p className="mt-3 text-sm text-background-200 md:text-base">{hero.tagline}</p>
                  <p className="mt-3 line-clamp-2 max-w-lg text-sm leading-relaxed text-background-200/90">
                    {comic.description}
                  </p>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="rounded-full border border-background-50/30 px-3 py-1 text-xs text-background-100"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${comic.id}`}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-book-open-line"></i>
                      立即阅读
                    </Link>
                    <Link
                      to={`/category/${comic.category}`}
                      className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10"
                    >
                      <i className="ri-layout-grid-line"></i>
                      同类推荐
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <div className="absolute bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {heroes.map((hero, index) => (
          <button
            key={hero.comicId}
            type="button"
            aria-label={`切换到第${index + 1}张`}
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? 'w-8 bg-primary-500' : 'w-4 bg-background-50/50 hover:bg-background-50/80'
            }`}
          ></button>
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => goTo(active - 1)}
        className="absolute left-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:flex cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => goTo(active + 1)}
        className="absolute right-3 top-1/2 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 md:flex cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>
    </section>
  );
}